import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Boxes, Receipt, Truck, Users2, Building2, Package, UserCog, Wallet, Plus, Pencil, Trash2, Zap, CheckCircle2, Ban, ClipboardList, Download, Play, TrendingUp, FileDown, ArrowRightLeft, PieChart as PieIcon, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import api, { downloadPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CITIES = ["Cape Town", "Johannesburg", "Durban", "Gqeberha", "Pretoria", "Bloemfontein", "East London", "Gaborone", "Maputo", "Harare", "Lusaka", "Windhoek"];
const err = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (!d) return fb;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join("; ");
  return fb;
};
const money = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
function exportCsv(filename, headers, rows) {
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename; a.click();
}

// Generic CRUD table + add/edit dialog
function ResourceManager({ endpoint, listKey, columns, fields, canWrite, title, testid, headerActions, rowActions, onMutate }) {
  const { data, loading, setData } = useFetch(() => api.get(endpoint).then((r) => r.data));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  if (loading) return <Spinner />;
  const rows = data[listKey] || [];
  const reload = async () => setData((await api.get(endpoint)).data);
  const blank = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "checkbox" ? false : "")]));
  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm(Object.fromEntries(fields.map((f) => [f.key, r[f.key] ?? (f.default ?? "")]))); setOpen(true); };

  const submit = async () => {
    for (const f of fields) if (f.required && !form[f.key] && !(editing && f.createOnly)) return toast.error(`${f.label} is required`);
    const payload = {};
    for (const f of fields) {
      const v = form[f.key];
      if (f.type === "number") { if (v === "" || v === null || v === undefined) continue; payload[f.key] = Number(v); }
      else payload[f.key] = v;
    }
    setBusy(true);
    try {
      if (editing) await api.patch(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Updated" : "Created"); setOpen(false); await reload(); if (onMutate) await onMutate();
    } catch (e) { toast.error(err(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async (r) => { if (!window.confirm("Delete this record? This cannot be undone.")) return; try { await api.delete(`${endpoint}/${r.id}`); toast.success("Deleted"); await reload(); if (onMutate) await onMutate(); } catch (e) { toast.error(err(e, "Delete failed")); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">{rows.length} record{rows.length === 1 ? "" : "s"}</p>
        <div className="flex gap-2">
          {headerActions && headerActions(reload)}
          {canWrite && <Button onClick={openNew} className="bg-primary hover:bg-red-700" data-testid={`${testid}-add`}><Plus size={16} className="mr-1.5" /> Add {title}</Button>}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{columns.map((c) => <th key={c.key} className="p-4">{c.label}</th>)}{canWrite && <th className="p-4 text-right">Actions</th>}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={columns.length + 1} className="p-6 text-muted-foreground">No records yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`${testid}-row-${r.id}`} className="hover:bg-muted/40">
                {columns.map((c) => <td key={c.key} className="p-4">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                {canWrite && (
                  <td className="p-4 text-right whitespace-nowrap">
                    {rowActions && rowActions(r, reload)}
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`${testid}-edit-${r.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`${testid}-del-${r.id}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={16} /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title}</DialogTitle><DialogDescription>Fields marked * are required.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => (editing && f.createOnly) ? null : (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "select" ? (
                  <select value={String(form[f.key] ?? "")} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-f-${f.key}`}
                    className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm">
                    <option value="">— select —</option>
                    {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
                  </select>
                ) : f.type === "checkbox" ? (
                  <div className="mt-2"><input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} data-testid={`${testid}-f-${f.key}`} className="h-4 w-4" /></div>
                ) : (
                  <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? e.target.value : e.target.value })} placeholder={f.placeholder} data-testid={`${testid}-f-${f.key}`} className="mt-1" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={submit} disabled={busy} className="bg-primary hover:bg-red-700 w-full" data-testid={`${testid}-save`}>{busy ? "Saving…" : editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Overview() {
  const { data, loading } = useFetch(() => api.get("/erp/summary").then((r) => r.data));
  if (loading) return <Spinner />;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard testid="erp-sum-invoices" label="Invoices" value={data.invoices.total} icon={Receipt} />
      <StatCard testid="erp-sum-outstanding" label="Outstanding" value={money(data.invoices.outstanding_zar)} icon={Wallet} accent="text-orange-600" />
      <StatCard testid="erp-sum-shipments" label="Active Shipments" value={data.shipments.active} icon={Package} accent="text-blue-600" />
      <StatCard testid="erp-sum-vehicles" label="Vehicles" value={data.vehicles} icon={Truck} />
      <StatCard testid="erp-sum-drivers" label="Drivers" value={data.drivers} icon={UserCog} accent="text-emerald-600" />
      <StatCard testid="erp-sum-employees" label="Employees" value={data.employees} icon={Users2} accent="text-violet-600" />
      <StatCard testid="erp-sum-suppliers" label="Suppliers" value={data.suppliers} icon={Building2} accent="text-teal-600" />
      <StatCard testid="erp-sum-companies" label="Clients" value={data.companies} icon={Building2} />
    </div>
  );
}

function GenerateInvoice({ reload }) {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [ships, setShips] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/erp/shipments").then((r) => setShips(r.data.shipments || [])); }, [open]);
  const go = async () => { if (!sid) return toast.error("Pick a shipment"); setBusy(true); try { const r = await api.post(`/erp/invoices/generate?shipment_id=${sid}`); toast.success(`Invoice ${r.data.invoice.invoice_number} generated`); setOpen(false); setSid(""); reload(); } catch (e) { toast.error(err(e, "Generate failed")); } finally { setBusy(false); } };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="erp-generate-open"><Zap size={16} className="mr-1.5" /> Generate from shipment</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="erp-generate-dialog">
          <DialogHeader><DialogTitle>Generate invoice</DialogTitle><DialogDescription>Uses the pricing engine to bill a shipment.</DialogDescription></DialogHeader>
          <select value={sid} onChange={(e) => setSid(e.target.value)} data-testid="erp-generate-select" className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm">
            <option value="">— select shipment —</option>
            {ships.map((s) => <option key={s.id} value={s.id}>{s.tracking_number} · {s.origin_city}→{s.dest_city} · {s.customer_name}</option>)}
          </select>
          <DialogFooter><Button onClick={go} disabled={busy} className="bg-primary hover:bg-red-700 w-full" data-testid="erp-generate-go">{busy ? "Generating…" : "Generate invoice"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PayrollTab() {
  const [lines, setLines] = useState(null);
  const [totals, setTotals] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadRuns = async () => setRuns((await api.get("/erp/payroll/runs")).data.runs || []);
  useEffect(() => { (async () => { const r = await api.get("/erp/payroll/preview"); setLines(r.data.lines); setTotals(r.data.totals); loadRuns(); })(); }, []);
  const run = async () => { setBusy(true); try { await api.post(`/erp/payroll/run?month=${month}`); toast.success(`Payroll saved for ${month}`); loadRuns(); } catch (e) { toast.error(err(e, "Run failed")); } setBusy(false); };
  if (!lines) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2 flex-wrap">
        <div><Label className="text-xs">Pay month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="erp-payroll-month" className="mt-1 w-44" /></div>
        <Button onClick={run} disabled={busy} className="bg-primary hover:bg-red-700" data-testid="erp-payroll-run"><Play size={16} className="mr-1.5" /> {busy ? "Running…" : "Run & Save"}</Button>
        <Button variant="outline" onClick={() => exportCsv(`payroll-preview-${month}.csv`, ["name", "position", "department", "gross_zar", "paye_zar", "uif_zar", "net_zar"], lines)} data-testid="erp-payroll-export"><Download size={16} className="mr-1.5" /> Export CSV</Button>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-3">Employee</th><th className="p-3">Dept</th><th className="p-3 text-right">Gross</th><th className="p-3 text-right">PAYE (est)</th><th className="p-3 text-right">UIF</th><th className="p-3 text-right">Net</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {lines.length === 0 && <tr><td colSpan={6} className="p-6 text-muted-foreground">No active employees. Add employees with a salary in the Employees tab.</td></tr>}
            {lines.map((l) => (
              <tr key={l.employee_id} data-testid={`erp-payroll-row-${l.employee_id}`} className="hover:bg-muted/40">
                <td className="p-3"><div className="font-semibold">{l.name}</div><div className="text-xs text-muted-foreground">{l.position} · {l.pay_cycle}</div></td>
                <td className="p-3 text-muted-foreground">{l.department || "—"}</td>
                <td className="p-3 text-right">{money(l.gross_zar)}</td>
                <td className="p-3 text-right text-orange-600">{money(l.paye_zar)}</td>
                <td className="p-3 text-right text-orange-600">{money(l.uif_zar)}</td>
                <td className="p-3 text-right font-semibold text-emerald-700 dark:text-emerald-400">{money(l.net_zar)}</td>
              </tr>
            ))}
            {totals && lines.length > 0 && (
              <tr className="bg-muted/30 font-bold" data-testid="erp-payroll-totals">
                <td className="p-3" colSpan={2}>{totals.headcount} employees</td>
                <td className="p-3 text-right">{money(totals.gross_zar)}</td>
                <td className="p-3 text-right">{money(totals.paye_zar)}</td>
                <td className="p-3 text-right">{money(totals.uif_zar)}</td>
                <td className="p-3 text-right text-emerald-700 dark:text-emerald-400">{money(totals.net_zar)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">PAYE is a simplified 18% estimate and UIF is 1% (capped) — for planning, not statutory filing.</p>
      {runs.length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <div className="p-3 border-b border-border font-heading font-bold text-sm">Saved payroll runs</div>
          <div className="divide-y divide-border">
            {runs.map((rn) => (
              <div key={rn.id} className="p-3 flex items-center justify-between text-sm" data-testid={`erp-payroll-savedrun-${rn.id}`}>
                <span><b>{rn.month}</b> · {rn.totals.headcount} staff · net {money(rn.totals.net_zar)} <span className="text-muted-foreground text-xs">by {rn.run_by}</span></span>
                <div className="flex items-center gap-1">
                  <PayslipDialog run={rn} />
                  <Button variant="ghost" size="sm" onClick={() => exportCsv(`payroll-${rn.month}.csv`, ["name", "position", "department", "gross_zar", "paye_zar", "uif_zar", "net_zar"], rn.lines)}><Download size={14} className="mr-1" /> CSV</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpensesTab({ opts, canWrite }) {
  const [prof, setProf] = useState(null);
  const loadProf = async () => setProf((await api.get("/erp/profitability")).data.trips || []);
  useEffect(() => { loadProf(); }, []);
  return (
    <div className="space-y-6">
      <ResourceManager endpoint="/erp/expenses" listKey="expenses" title="Expense" testid="erp-exp" canWrite={canWrite} onMutate={loadProf}
        columns={[
          { key: "date", label: "Date", render: (r) => (r.date || "").slice(0, 10) },
          { key: "category", label: "Category" },
          { key: "vehicle_reg", label: "Vehicle", render: (r) => r.vehicle_reg || "—" },
          { key: "tracking_number", label: "Trip", render: (r) => r.tracking_number || "—" },
          { key: "amount_zar", label: "Amount", render: (r) => money(r.amount_zar) },
          { key: "note", label: "Note / Source", render: (r) => r.po_id ? <span className="inline-flex items-center gap-1 text-xs"><span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-500/15 font-semibold">PO</span><span className="text-muted-foreground">{r.note || "—"}</span></span> : <span className="text-muted-foreground text-xs">{r.note || "—"}</span> },
        ]}
        fields={[
          { key: "category", label: "Category", type: "select", options: ["Fuel", "Maintenance", "Tolls", "Tyres", "Fines", "Other"].map((s) => ({ value: s, label: s })), default: "Fuel" },
          { key: "vehicle_id", label: "Vehicle", type: "select", options: opts.vehicles },
          { key: "shipment_id", label: "Trip / shipment", type: "select", options: opts.shipments },
          { key: "amount_zar", label: "Amount (ZAR)", type: "number", required: true },
          { key: "date", label: "Date", type: "date" },
          { key: "note", label: "Note", full: true },
        ]} />
      <div>
        <h3 className="font-heading font-bold text-sm flex items-center gap-2 mb-3"><TrendingUp size={16} className="text-primary" /> Profit per trip <span className="text-xs text-muted-foreground font-normal">(invoice revenue − logged costs)</span></h3>
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="p-3">Trip</th><th className="p-3">Route</th><th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Cost</th><th className="p-3 text-right">Profit</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(!prof || prof.length === 0) && <tr><td colSpan={5} className="p-6 text-muted-foreground">Link expenses and invoices to a shipment to see profit per trip.</td></tr>}
              {(prof || []).map((t) => (
                <tr key={t.shipment_id} data-testid={`erp-profit-row-${t.shipment_id}`} className="hover:bg-muted/40">
                  <td className="p-3 font-mono font-semibold">{t.tracking_number}</td>
                  <td className="p-3 text-muted-foreground">{t.route}</td>
                  <td className="p-3 text-right">{money(t.revenue_zar)}</td>
                  <td className="p-3 text-right text-orange-600">{money(t.cost_zar)}</td>
                  <td className={`p-3 text-right font-bold ${t.profit_zar >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-600"}`}>{money(t.profit_zar)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const COST_COLORS = { Fuel: "#DC2626", Maintenance: "#2563EB", Tolls: "#F59E0B", Tyres: "#10B981", Fines: "#8B5CF6", Other: "#64748B" };

function CostsTab() {
  const { data, loading } = useFetch(() => api.get("/erp/costs/overview").then((r) => r.data));
  if (loading) return <Spinner />;
  const cats = data.categories || [];
  const primary = ["Fuel", "Maintenance", "Tolls"];
  const pie = (data.by_category || []).filter((c) => c.amount_zar > 0);
  const empty = (data.total_zar || 0) === 0;
  const alerts = data.alerts || [];
  return (
    <div className="space-y-6">
      {alerts.length > 0 && (
        <div className="rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 p-4" data-testid="erp-costs-alerts">
          <div className="flex items-center gap-2 font-heading font-bold text-sm text-red-700 dark:text-red-400 mb-2"><AlertTriangle size={16} /> Cost spike alerts — vehicles running above their 3-month average</div>
          <div className="space-y-1.5">
            {alerts.map((a) => (
              <div key={a.vehicle_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`erp-costs-alert-${a.vehicle_id}`}>
                <span><span className="font-mono font-semibold">{a.vehicle_reg}</span> <span className="text-muted-foreground">· {a.branch} · {a.month}</span></span>
                <span><span className="font-semibold text-red-600">{money(a.current_zar)}</span> <span className="text-muted-foreground">vs avg {money(a.avg_zar)}</span> <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 text-xs font-bold">+{a.pct_over}%</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="erp-costs-total" label="Total costs" value={money(data.total_zar)} icon={Wallet} accent="text-primary" />
        {(data.by_category || []).filter((c) => primary.includes(c.category)).map((c) => (
          <StatCard key={c.category} testid={`erp-costs-cat-${c.category}`} label={c.category} value={money(c.amount_zar)} icon={TrendingUp} />
        ))}
      </div>
      {empty ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="erp-costs-empty">
          No costs logged yet. Add expenses in the Expenses tab (or convert received purchase orders) to see trends here.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card p-4" data-testid="erp-costs-monthly">
            <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-primary" /> Monthly cost trend</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
                {cats.map((c) => <Bar key={c} dataKey={c} stackId="a" fill={COST_COLORS[c] || "#94a3b8"} radius={c === cats[cats.length - 1] ? [4, 4, 0, 0] : 0} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-border bg-card p-4" data-testid="erp-costs-branch">
              <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><Building2 size={16} className="text-primary" /> Costs by branch</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_branch} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" fontSize={11} tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="branch" fontSize={11} width={90} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend />
                  {primary.map((c) => <Bar key={c} dataKey={c} stackId="b" fill={COST_COLORS[c]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="erp-costs-split">
              <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><PieIcon size={16} className="text-primary" /> Cost mix</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pie} dataKey="amount_zar" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                    {pie.map((e) => <Cell key={e.category} fill={COST_COLORS[e.category] || "#94a3b8"} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="erp-costs-vehicle">
            <div className="p-3 border-b border-border font-heading font-bold text-sm flex items-center gap-2"><Truck size={16} className="text-primary" /> Top vehicles by cost</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="p-3">Vehicle</th>{primary.map((c) => <th key={c} className="p-3 text-right">{c}</th>)}<th className="p-3 text-right">Total</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.by_vehicle.map((v) => (
                  <tr key={v.vehicle} className="hover:bg-muted/40" data-testid={`erp-costs-veh-${v.vehicle}`}>
                    <td className="p-3 font-mono font-semibold">{v.vehicle}</td>
                    {primary.map((c) => <td key={c} className="p-3 text-right text-muted-foreground">{money(v[c])}</td>)}
                    <td className="p-3 text-right font-bold">{money(v.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PayslipDialog({ run }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const dl = async (l) => {
    setBusy(l.employee_id);
    try { await downloadPdf(`/erp/payroll/runs/${run.id}/payslip/${l.employee_id}`, `payslip-${run.month}-${l.name}.pdf`); }
    catch (e) { toast.error(err(e, "Download failed")); }
    finally { setBusy(""); }
  };
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid={`erp-payslip-open-${run.id}`}><FileDown size={14} className="mr-1" /> Payslips</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid={`erp-payslip-dialog-${run.id}`}>
          <DialogHeader><DialogTitle>Payslips — {run.month}</DialogTitle><DialogDescription>Download a branded PDF payslip per employee.</DialogDescription></DialogHeader>
          <div className="divide-y divide-border">
            {(run.lines || []).map((l) => (
              <div key={l.employee_id} className="py-2.5 flex items-center justify-between gap-3">
                <div><div className="font-semibold text-sm">{l.name}</div><div className="text-xs text-muted-foreground">{l.position || "—"} · net {money(l.net_zar)}</div></div>
                <Button variant="outline" size="sm" onClick={() => dl(l)} disabled={busy === l.employee_id} data-testid={`erp-payslip-dl-${l.employee_id}`}><Download size={14} className="mr-1" /> {busy === l.employee_id ? "…" : "PDF"}</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ErpConsole() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "managing_director", "operations_manager", "fleet_manager", "finance_manager"].includes(user?.role);
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    (async () => {
      const [c, f, r, sup, sh] = await Promise.all([
        api.get("/admin/companies").catch(() => ({ data: { companies: [] } })),
        api.get("/fleet").catch(() => ({ data: { drivers: [], vehicles: [] } })),
        api.get("/meta/roles").catch(() => ({ data: { roles: [] } })),
        api.get("/erp/suppliers").catch(() => ({ data: { suppliers: [] } })),
        api.get("/erp/shipments").catch(() => ({ data: { shipments: [] } })),
      ]);
      setOpts({
        companies: (c.data.companies || []).map((x) => ({ value: x.id, label: x.name })),
        drivers: (f.data.drivers || []).map((x) => ({ value: x.id, label: `${x.name} (${x.id})` })),
        vehicles: (f.data.vehicles || []).map((x) => ({ value: x.id, label: `${x.reg_no} (${x.id})` })),
        roles: (r.data.roles || []).map((x) => ({ value: x.value, label: x.label })),
        suppliers: (sup.data.suppliers || []).map((x) => ({ value: x.id, label: x.name })),
        shipments: (sh.data.shipments || []).map((x) => ({ value: x.id, label: `${x.tracking_number} · ${x.origin_city}→${x.dest_city}` })),
      });
    })();
  }, []);
  if (!opts) return <Spinner />;

  const STATUS = (arr) => arr.map((s) => ({ value: s, label: s }));

  return (
    <div className="pb-28">
      <PageHeader title="ERP Console" subtitle="Invoicing · Transport · Employees · Suppliers — one command center" />
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="erp-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="invoicing" data-testid="erp-tab-invoicing">Invoicing</TabsTrigger>
          <TabsTrigger value="shipments" data-testid="erp-tab-shipments">Shipments</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="erp-tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="erp-tab-drivers">Drivers</TabsTrigger>
          <TabsTrigger value="employees" data-testid="erp-tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="erp-tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="purchase" data-testid="erp-tab-purchase">Purchase Orders</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="erp-tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="erp-tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="costs" data-testid="erp-tab-costs">Costs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><Overview /></TabsContent>

        <TabsContent value="invoicing">
          <ResourceManager endpoint="/erp/invoices" listKey="invoices" title="Invoice" testid="erp-inv" canWrite={canWrite}
            headerActions={(reload) => <GenerateInvoice reload={reload} />}
            columns={[
              { key: "invoice_number", label: "Number", render: (r) => <span className="font-mono font-semibold">{r.invoice_number}</span> },
              { key: "customer_name", label: "Customer" },
              { key: "total_zar", label: "Total", render: (r) => money(r.total_zar) },
              { key: "status", label: "Status", render: (r) => <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === "Paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : r.status === "Overdue" ? "bg-red-100 text-red-700 dark:bg-red-500/15" : r.status === "Void" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15"}`}>{r.status}</span> },
            ]}
            rowActions={(r, reload) => (
              <>
                {r.status !== "Paid" && <Button variant="ghost" size="icon" title="Mark paid" onClick={async () => { await api.post(`/erp/invoices/${r.id}/status?status=Paid`); toast.success("Marked paid"); reload(); }} data-testid={`erp-inv-paid-${r.id}`} className="text-emerald-600"><CheckCircle2 size={16} /></Button>}
                {r.status !== "Void" && <Button variant="ghost" size="icon" title="Void" onClick={async () => { await api.post(`/erp/invoices/${r.id}/status?status=Void`); toast.success("Voided"); reload(); }} data-testid={`erp-inv-void-${r.id}`} className="text-slate-500"><Ban size={16} /></Button>}
              </>
            )}
            fields={[
              { key: "customer_name", label: "Customer name", required: true, full: true },
              { key: "company_id", label: "Client company", type: "select", options: opts.companies },
              { key: "amount_zar", label: "Amount (ZAR, excl. tax)", type: "number", required: true },
              { key: "tax_pct", label: "Tax %", type: "number", default: 15, createOnly: true },
              { key: "due_days", label: "Due in (days)", type: "number", default: 30, createOnly: true },
              { key: "status", label: "Status", type: "select", options: STATUS(["Outstanding", "Paid", "Overdue", "Void", "Draft"]), default: "Outstanding" },
            ]} />
        </TabsContent>

        <TabsContent value="shipments">
          <ResourceManager endpoint="/erp/shipments" listKey="shipments" title="Shipment" testid="erp-ship" canWrite={canWrite}
            columns={[
              { key: "tracking_number", label: "Tracking", render: (r) => <span className="font-mono font-semibold">{r.tracking_number}</span> },
              { key: "route", label: "Route", render: (r) => `${r.origin_city} → ${r.dest_city}` },
              { key: "status", label: "Status" },
              { key: "driver_id", label: "Driver" },
              { key: "vehicle_id", label: "Vehicle" },
            ]}
            fields={[
              { key: "customer_name", label: "Customer name", required: true, full: true },
              { key: "customer_company_id", label: "Client company", type: "select", options: opts.companies },
              { key: "origin_city", label: "Origin", type: "select", options: CITIES, required: true },
              { key: "dest_city", label: "Destination", type: "select", options: CITIES, required: true },
              { key: "cargo_type", label: "Cargo type" },
              { key: "weight_kg", label: "Weight (kg)", type: "number" },
              { key: "priority", label: "Priority", type: "select", options: STATUS(["Standard", "Express", "Critical"]), default: "Standard" },
              { key: "service_type", label: "Service", type: "select", options: STATUS(["Full Truckload (FTL)", "Less-than-Truckload (LTL)", "Container Transport", "Cross-Border", "Last-Mile Distribution", "Port-to-Warehouse"]) },
              { key: "status", label: "Status", type: "select", options: STATUS(["Pending Pickup", "In Transit", "Delayed", "At Risk", "Exception", "Delivered"]) },
              { key: "driver_id", label: "Assign driver", type: "select", options: opts.drivers },
              { key: "vehicle_id", label: "Assign vehicle", type: "select", options: opts.vehicles },
            ]} />
        </TabsContent>

        <TabsContent value="vehicles">
          <ResourceManager endpoint="/erp/vehicles" listKey="vehicles" title="Vehicle" testid="erp-veh" canWrite={canWrite}
            columns={[
              { key: "reg_no", label: "Reg No", render: (r) => <span className="font-mono font-semibold">{r.reg_no}</span> },
              { key: "type", label: "Type" }, { key: "branch", label: "Branch" },
              { key: "status", label: "Status" }, { key: "driver_id", label: "Driver" },
            ]}
            fields={[
              { key: "reg_no", label: "Registration", required: true },
              { key: "type", label: "Type", type: "select", options: STATUS(["FTL", "LTL", "Container", "Refrigerated", "Flatbed", "Tanker"]), default: "FTL" },
              { key: "capacity_kg", label: "Capacity (kg)", type: "number", default: 14000 },
              { key: "capacity_cbm", label: "Capacity (cbm)", type: "number", default: 40 },
              { key: "branch", label: "Branch", type: "select", options: CITIES, default: "Johannesburg" },
              { key: "status", label: "Status", type: "select", options: STATUS(["Available", "In Transit", "Loading", "Maintenance"]), default: "Available" },
              { key: "driver_id", label: "Driver", type: "select", options: opts.drivers },
              { key: "fuel_level", label: "Fuel %", type: "number", default: 100 },
              { key: "mileage_km", label: "Mileage (km)", type: "number", default: 0 },
            ]} />
        </TabsContent>

        <TabsContent value="drivers">
          <ResourceManager endpoint="/erp/drivers" listKey="drivers" title="Driver" testid="erp-drv" canWrite={canWrite}
            columns={[
              { key: "name", label: "Name" }, { key: "phone", label: "Phone" },
              { key: "license_no", label: "License" }, { key: "branch", label: "Branch" }, { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "name", label: "Full name", required: true, full: true },
              { key: "phone", label: "Phone" },
              { key: "license_no", label: "License number" },
              { key: "license_class", label: "License class", type: "select", options: STATUS(["EC", "EC1", "C1", "C"]), default: "EC" },
              { key: "branch", label: "Branch", type: "select", options: CITIES, default: "Johannesburg" },
              { key: "status", label: "Status", type: "select", options: STATUS(["Available", "On Duty", "Off Duty"]), default: "Available" },
              { key: "vehicle_id", label: "Assigned vehicle", type: "select", options: opts.vehicles },
            ]} />
        </TabsContent>

        <TabsContent value="employees">
          <ResourceManager endpoint="/erp/employees" listKey="employees" title="Employee" testid="erp-emp" canWrite={canWrite}
            columns={[
              { key: "name", label: "Name" }, { key: "position", label: "Position" },
              { key: "department", label: "Department" }, { key: "branch", label: "Branch" },
              { key: "status", label: "Status" },
              { key: "user_id", label: "Login", render: (r) => r.user_id ? <span className="text-emerald-600 text-xs font-semibold">✓ account</span> : "—" },
            ]}
            fields={[
              { key: "name", label: "Full name", required: true },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "position", label: "Position" },
              { key: "department", label: "Department", type: "select", options: STATUS(["Operations", "Fleet", "Finance", "Warehouse", "HR", "Sales", "Management"]) },
              { key: "branch", label: "Branch", type: "select", options: CITIES },
              { key: "status", label: "Status", type: "select", options: STATUS(["Active", "On Leave", "Suspended", "Terminated"]), default: "Active" },
              { key: "hire_date", label: "Hire date", type: "date" },
              { key: "salary_zar", label: "Salary (ZAR)", type: "number" },
              { key: "pay_cycle", label: "Pay cycle", type: "select", options: STATUS(["Monthly", "Bi-weekly", "Weekly"]), default: "Monthly" },
              { key: "create_login", label: "Create login account", type: "checkbox", createOnly: true },
              { key: "login_password", label: "Login password (min 8)", type: "password", createOnly: true },
              { key: "login_role", label: "Login role", type: "select", options: opts.roles, createOnly: true, default: "operations_manager" },
            ]} />
        </TabsContent>

        <TabsContent value="suppliers">
          <ResourceManager endpoint="/erp/suppliers" listKey="suppliers" title="Supplier" testid="erp-sup" canWrite={canWrite}
            columns={[
              { key: "name", label: "Name" }, { key: "category", label: "Category" },
              { key: "contact_person", label: "Contact" }, { key: "phone", label: "Phone" }, { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "name", label: "Supplier name", required: true, full: true },
              { key: "category", label: "Category", type: "select", options: STATUS(["Fuel", "Maintenance", "Tyres", "Insurance", "Logistics Partner", "Parts", "Other"]), default: "Other" },
              { key: "contact_person", label: "Contact person" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "address", label: "Address", full: true },
              { key: "payment_terms", label: "Payment terms", type: "select", options: STATUS(["On Receipt", "7 days", "30 days", "60 days"]), default: "30 days" },
              { key: "status", label: "Status", type: "select", options: STATUS(["Active", "Inactive"]), default: "Active" },
            ]} />
        </TabsContent>

        <TabsContent value="purchase">
          <ResourceManager endpoint="/erp/purchase-orders" listKey="purchase_orders" title="Purchase Order" testid="erp-po" canWrite={canWrite}
            columns={[
              { key: "po_number", label: "PO #", render: (r) => <span className="font-mono font-semibold">{r.po_number}</span> },
              { key: "supplier_name", label: "Supplier" },
              { key: "category", label: "Category" },
              { key: "total_zar", label: "Total", render: (r) => money(r.total_zar) },
              { key: "status", label: "Status", render: (r) => <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === "Paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : r.status === "Received" ? "bg-blue-100 text-blue-700 dark:bg-blue-500/15" : r.status === "Cancelled" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15"}`}>{r.status}</span> },
            ]}
            rowActions={(r, reload) => (
              <>
                {r.status !== "Received" && r.status !== "Paid" && <Button variant="ghost" size="icon" title="Mark received" onClick={async () => { await api.post(`/erp/purchase-orders/${r.id}/status?status=Received`); toast.success("Received"); reload(); }} data-testid={`erp-po-received-${r.id}`} className="text-blue-600"><ClipboardList size={16} /></Button>}
                {(r.status === "Received" || r.status === "Paid") && !r.expense_id && <Button variant="ghost" size="icon" title="Log as expense" onClick={async () => { try { await api.post(`/erp/purchase-orders/${r.id}/to-expense`); toast.success("Logged as expense"); reload(); } catch (e) { toast.error(err(e, "Failed")); } }} data-testid={`erp-po-toexp-${r.id}`} className="text-violet-600"><ArrowRightLeft size={16} /></Button>}
                {r.expense_id && <span title="Logged as expense" className="inline-flex items-center px-2 text-xs font-semibold text-violet-600"><ArrowRightLeft size={13} className="mr-1" />logged</span>}
                {r.status !== "Paid" && <Button variant="ghost" size="icon" title="Mark paid" onClick={async () => { await api.post(`/erp/purchase-orders/${r.id}/status?status=Paid`); toast.success("Paid"); reload(); }} data-testid={`erp-po-paid-${r.id}`} className="text-emerald-600"><CheckCircle2 size={16} /></Button>}
              </>
            )}
            fields={[
              { key: "supplier_id", label: "Supplier", type: "select", options: opts.suppliers, required: true },
              { key: "category", label: "Category", type: "select", options: ["Fuel", "Maintenance", "Tyres", "Parts", "Insurance", "Services", "Other"].map((s) => ({ value: s, label: s })), default: "Other" },
              { key: "description", label: "Description", full: true },
              { key: "amount_zar", label: "Amount (ZAR, excl. tax)", type: "number", required: true },
              { key: "tax_pct", label: "Tax %", type: "number", default: 15, createOnly: true },
              { key: "expected_date", label: "Expected date", type: "date" },
              { key: "status", label: "Status", type: "select", options: ["Draft", "Ordered", "Received", "Cancelled", "Paid"].map((s) => ({ value: s, label: s })), default: "Draft" },
            ]} />
        </TabsContent>

        <TabsContent value="payroll"><PayrollTab /></TabsContent>
        <TabsContent value="expenses"><ExpensesTab opts={opts} canWrite={canWrite} /></TabsContent>
        <TabsContent value="costs"><CostsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
